/**
 * Error Handling Utilities (No JSX)
 * Pure JavaScript utilities for error handling and crash prevention
 */

/* global navigator, performance */

/**
 * Safe state updater
 */
export const safeSetState = (setState, newState) => {
  try {
    if (typeof setState === "function") {
      setState(newState);
    }
  } catch (error) {
    console.error("State update error:", error);
  }
};

/**
 * Safe array access
 */
export const safeArrayAccess = (array, index, fallback = null) => {
  try {
    if (Array.isArray(array) && array.length > index && index >= 0) {
      return array[index];
    }
    return fallback;
  } catch (error) {
    console.error("Array access error:", error);
    return fallback;
  }
};

/**
 * Safe object property access
 */
export const safeGet = (obj, path, fallback = null) => {
  try {
    const keys = path.split(".");
    let result = obj;

    for (const key of keys) {
      if (result && typeof result === "object" && key in result) {
        result = result[key];
      } else {
        return fallback;
      }
    }

    return result;
  } catch (error) {
    console.error("Object access error:", error);
    return fallback;
  }
};

/**
 * Safe number formatting
 */
export const safeFormatNumber = (value, options = {}) => {
  try {
    const num = parseFloat(value);
    if (isNaN(num)) return "0";

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(num);
  } catch (error) {
    console.error("Number formatting error:", error);
    return "0";
  }
};

/**
 * Safe date formatting
 */
export const safeFormatDate = (date, options = {}) => {
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "Invalid Date";

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...options,
    }).format(dateObj);
  } catch (error) {
    console.error("Date formatting error:", error);
    return "Invalid Date";
  }
};

/**
 * Safe localStorage access
 */
export const safeLocalStorage = {
  get: (key, fallback = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : fallback;
    } catch (error) {
      console.error("LocalStorage get error:", error);
      return fallback;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error("LocalStorage set error:", error);
      return false;
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error("LocalStorage remove error:", error);
      return false;
    }
  },
};

/**
 * Debounce function to prevent excessive API calls
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit function execution
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Network status checker
 */
export const checkNetworkStatus = () => {
  return {
    online: navigator.onLine,
    connection:
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection,
  };
};

/**
 * Performance monitor
 */
export const performanceMonitor = {
  mark: (name) => {
    try {
      performance.mark(name);
    } catch (error) {
      console.warn("Performance mark error:", error);
    }
  },

  measure: (name, startMark, endMark) => {
    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name)[0];
      return measure ? measure.duration : 0;
    } catch (error) {
      console.warn("Performance measure error:", error);
      return 0;
    }
  },
};

/**
 * Memory usage monitor
 */
export const memoryMonitor = {
  getUsage: () => {
    try {
      if (performance.memory) {
        return {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit,
        };
      }
      return null;
    } catch (error) {
      console.warn("Memory monitor error:", error);
      return null;
    }
  },
};

/**
 * Error reporter
 */
export const errorReporter = {
  report: (error, context = {}) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context,
    };

    console.error("Error Report:", errorData);

    if (window.errorTracker) {
      window.errorTracker.report(errorData);
    }
  },
};

/**
 * Global error handler setup
 */
export const setupGlobalErrorHandling = () => {
  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled promise rejection:", event.reason);
    errorReporter.report(new Error(event.reason), {
      type: "unhandledrejection",
    });
  });

  window.addEventListener("error", (event) => {
    console.error("Global error:", event.error);
    errorReporter.report(event.error, { type: "global" });
  });

  window.addEventListener("react-error", (event) => {
    console.error("React error:", event.detail);
    errorReporter.report(new Error(event.detail.error), {
      type: "react",
      componentStack: event.detail.errorInfo,
    });
  });
};

export default {
  safeSetState,
  safeArrayAccess,
  safeGet,
  safeFormatNumber,
  safeFormatDate,
  safeLocalStorage,
  debounce,
  throttle,
  checkNetworkStatus,
  performanceMonitor,
  memoryMonitor,
  errorReporter,
  setupGlobalErrorHandling,
};
